import { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import Dialog from "../../../designSystem/c-organism/dialogs/Dialog";
import Input from "../../../designSystem/b-molecules/formElements/input/Input";
import Button from "../../../designSystem/b-molecules/buttons/Button";
import { onValue, ref, push, update, remove } from "firebase/database";
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, app } from "../../../configs/Firebase";
import Fuse from "fuse.js";
import style from "./ManageBrands.module.css";
import Icon from "../../../designSystem/a-atoms/icons/Icon";

const ManageBrands = ({ show, onClose, inline = false }) => {
  const org = useSelector((state) => state.orgStatus.value);
  const [brands, setBrands] = useState([]);
  const [creatingNew, setCreatingNew] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [newBrandName, setNewBrandName] = useState("");
  const [error, setError] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const imageInputRef = useRef(null);
  const storage = getStorage(app);

  useEffect(() => {
    if (org.payload && org.payload !== undefined) {
      const brandsRef = ref(db, `userData/${org.payload}/brands`);

      onValue(brandsRef, (snapshot) => {
        const data = snapshot.val();
        const temp = [];
        if (data) {
          for (let i in data) {
            temp.push({
              id: i,
              ...data[i],
            });
          }
        }
        setBrands(temp);
      });
    }
  }, [org]);

  const fuse = new Fuse(brands, {
    keys: [{ name: "name" }],
    includeMatches: true,
  });
  const [brandResults, setBrandResults] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  function searchHandler(e) {
    setError("");
    const value = e.target.value;
    setSearchTerm(value);
    if (value) {
      setBrandResults(fuse.search(value));
    } else {
      setBrandResults([]);
    }
  }

  const handleImageChange = (file) => {
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleImageChange(e.dataTransfer.files[0]);
    }
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleImageChange(e.target.files[0]);
    }
  };

  const handleImageButtonClick = (e) => {
    e.preventDefault();
    imageInputRef.current?.click();
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (imageInputRef.current) {
      imageInputRef.current.value = "";
    }
  };

  async function createNew() {
    if (!newBrandName.trim()) {
      setError("Brand name cannot be empty");
      return;
    }
    
    // Check if brand already exists
    if (brands.some(b => b.name.toLowerCase() === newBrandName.trim().toLowerCase())) {
      setError("This brand already exists");
      return;
    }

    if (org.payload) {
      const brandsRef = ref(db, `userData/${org.payload}/brands`);
      const newBrand = {
        name: newBrandName.trim(),
        createdAt: { ".sv": "timestamp" }
      };
      const snapshot = await push(brandsRef, newBrand);
      const brandId = snapshot.key;

      // Upload image if selected
      if (imageFile && org.payload) {
        try {
          const extension = imageFile.type.replace(/(.*)\//g, "");
          const uuid = crypto.randomUUID();
          const filename = uuid + "." + extension;
          const imageRef = storageRef(storage, `${org.payload}/brands/${brandId}/${filename}`);
          await uploadBytes(imageRef, imageFile);
          const imageURL = await getDownloadURL(imageRef);
          // Update brand with imageURL
          const brandRef = ref(db, `userData/${org.payload}/brands/${brandId}`);
          await update(brandRef, { imageURL });
        } catch (err) {
          console.error("Error uploading brand image:", err);
          setError("Failed to upload image: " + err.message);
        }
      }

      setNewBrandName("");
      setCreatingNew(false);
      setError("");
      setImageFile(null);
      setImagePreview(null);
      if (imageInputRef.current) {
        imageInputRef.current.value = "";
      }
    }
  }

  function startEdit(brand) {
    setEditingBrand(brand);
    setNewBrandName(brand.name);
    setCreatingNew(false);
    setError("");
    
    // Load existing image if available
    if (brand.imageURL && org.payload) {
      setImagePreview(brand.imageURL);
      setImageFile(null);
    } else {
      setImagePreview(null);
      setImageFile(null);
    }
    if (imageInputRef.current) {
      imageInputRef.current.value = "";
    }
  }

  async function saveEdit() {
    if (!newBrandName.trim()) {
      setError("Brand name cannot be empty");
      return;
    }

    // Check if another brand with this name exists
    if (brands.some(b => b.id !== editingBrand.id && b.name.toLowerCase() === newBrandName.trim().toLowerCase())) {
      setError("This brand name already exists");
      return;
    }

    if (org.payload && editingBrand) {
      const brandRef = ref(db, `userData/${org.payload}/brands/${editingBrand.id}`);
      const updateData = {
        name: newBrandName.trim()
      };

      // Upload new image if selected
      if (imageFile && org.payload) {
        try {
          const extension = imageFile.type.replace(/(.*)\//g, "");
          const uuid = crypto.randomUUID();
          const filename = uuid + "." + extension;
          const imageRef = storageRef(storage, `${org.payload}/brands/${editingBrand.id}/${filename}`);
          await uploadBytes(imageRef, imageFile);
          const imageURL = await getDownloadURL(imageRef);
          updateData.imageURL = imageURL;
        } catch (err) {
          console.error("Error uploading brand image:", err);
          setError("Failed to upload image: " + err.message);
          return;
        }
      }

      await update(brandRef, updateData);
      setEditingBrand(null);
      setNewBrandName("");
      setError("");
      setImageFile(null);
      setImagePreview(null);
      if (imageInputRef.current) {
        imageInputRef.current.value = "";
      }
    }
  }

  function deleteBrand(brandId) {
    if (org.payload) {
      const brandRef = ref(db, `userData/${org.payload}/brands/${brandId}`);
      remove(brandRef);
    }
  }

  function cancel() {
    setCreatingNew(false);
    setEditingBrand(null);
    setNewBrandName("");
    setError("");
    setImageFile(null);
    setImagePreview(null);
    if (imageInputRef.current) {
      imageInputRef.current.value = "";
    }
  }

  const displayBrands = searchTerm ? brandResults.map(r => r.item) : brands;

  const content = (
    <>
      <div className={style.input}>
        <Input
          label="Search Brands"
          placeholder=""
          onChange={searchHandler}
          value={searchTerm}
          error={error}
        />
      </div>

      {(creatingNew || editingBrand) ? (
        <div className={style.editSection}>
          <Input
            label="Brand Name"
            placeholder="Enter brand name"
            onChange={(e) => setNewBrandName(e.target.value)}
            value={newBrandName}
            error={error}
          />
          <div className={style.imageUpload}>
            <label>Brand Image</label>
            <div 
              className={`${style.imagePreviewContainer} ${dragActive ? style.dragActive : ''}`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              {imagePreview ? (
                <>
                  <img src={imagePreview} alt="Preview" className={style.imagePreview} />
                  <Button
                    variants={["text", "small"]}
                    onClick={handleRemoveImage}
                  >
                    <Icon>delete</Icon> Remove
                  </Button>
                </>
              ) : (
                <div className={style.imagePlaceholder}>
                  <Icon>image</Icon>
                </div>
              )}
              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileInputChange}
                style={{ display: 'none' }}
                id="image-upload"
              />
              <Button
                variants={["text", "small"]}
                onClick={handleImageButtonClick}
              >
                {imagePreview ? "Change Image" : "Upload Image"}
              </Button>
            </div>
          </div>
          <div className={style.actions}>
            <Button variants={["text"]} onClick={cancel}>
              Cancel
            </Button>
            <Button
              variants={["primary"]}
              onClick={editingBrand ? saveEdit : createNew}
            >
              {editingBrand ? "Save" : "Create"}
            </Button>
          </div>
        </div>
      ) : (
        <>
          <ul className={style.brandList}>
            {displayBrands.length > 0 ? (
              displayBrands.map((brand) => (
                <li key={brand.id}>
                  <span className={style.brandName}>{brand.name}</span>
                  <div className={style.brandActions}>
                    <Button
                      variants={["text", "small"]}
                      onClick={() => startEdit(brand)}
                    >
                      <Icon>edit</Icon>
                    </Button>
                    <Button
                      variants={["text", "small"]}
                      onClick={() => {
                        if (window.confirm(`Are you sure you want to delete "${brand.name}"?`)) {
                          deleteBrand(brand.id);
                        }
                      }}
                    >
                      <Icon>delete</Icon>
                    </Button>
                  </div>
                </li>
              ))
            ) : (
              <li className={style.noResults}>
                {searchTerm ? "No brands found" : "No brands created yet"}
              </li>
            )}
          </ul>
          <div className={style.actions}>
            <Button variants={["text"]} onClick={() => setCreatingNew(true)}>
              Create New Brand
            </Button>
            {!inline && (
              <Button variants={["text"]} onClick={onClose}>
                Close
              </Button>
            )}
          </div>
        </>
      )}
    </>
  );

  if (inline) {
    return content;
  }

  return (
    <Dialog show={show} onClose={onClose} title="Manage Brands">
      {content}
    </Dialog>
  );
};

export default ManageBrands;

